import AccordionButton from '@/components/accordion-button';
import Button from '@/components/button';
import Card from '@/components/card';
import CardHeader from '@/components/card/card-header';
import CardRow from '@/components/card/card-row';
import NoneChip from '@/components/chip/none-chip';
import StatusChip from '@/components/chip/status-chip';
import Text from '@/components/text';
import useToast from '@/hooks/use-toast.hook';
import { LoadState } from '@/lib/helpers';
import { TelemetryModel } from '@/lib/models/telemetry/telemetry.model';
import { TelemetryTestReport, testTelemetry } from '@/lib/services/telemetry.service';
import { Box, Stack } from '@mui/material';
import { FC, useState } from 'react';

interface Props {
  telemetry: TelemetryModel;
  onTest: () => void;
}

const TelemetryTestCard: FC<Props> = ({ telemetry, onTest }) => {
  const [testLoadState, setTestLoadState] = useState<LoadState>('unloaded');
  const [report, setReport] = useState<TelemetryTestReport>({
    found: false,
    canWrite: false,
    accessVerified: false,
    messages: [],
  });

  const { errorToast } = useToast();

  const handleTestSettings = () => {
    setTestLoadState('loading');

    testTelemetry(telemetry).then((report) => {
      if (!report) {
        setTestLoadState('unloaded');
        errorToast('Unable to run test. Please check that the connector is active.');
        return;
      }

      setTestLoadState('loaded');
      setReport(report);
      onTest();
    });
  };

  const loaded = testLoadState === 'loaded';
  const { found, canWrite, accessVerified, messages } = report;
  const hasMessages = !!messages.length;

  return (
    <Card>
      <CardHeader title="Destination Test" icon="test">
        <Button
          label="Run Test"
          onClick={handleTestSettings}
          type="submit"
          loading={testLoadState === 'loading'}
          size="small"
        />
      </CardHeader>
      <Stack gap={1}>
        <CardRow>
          <Stack direction="row" justifyContent="space-between">
            <Text>Destination found</Text>
            {!loaded && <NoneChip notAvailable />}
            {loaded && <StatusChip value={found ? 'yes' : 'no'} size="small" />}
          </Stack>
        </CardRow>
        <CardRow>
          <Stack direction="row" justifyContent="space-between">
            <Text>Destination writeable</Text>
            {!loaded && <NoneChip notAvailable />}
            {loaded && <StatusChip value={canWrite ? 'yes' : 'no'} size="small" />}
          </Stack>
        </CardRow>
        <CardRow>
          <Stack direction="row" justifyContent="space-between">
            <Text>Access verified</Text>
            {!loaded && <NoneChip notAvailable />}
            {loaded && <StatusChip value={accessVerified ? 'yes' : 'no'} size="small" />}
          </Stack>
        </CardRow>

        <AccordionButton title="Details">
          {!hasMessages && <NoneChip />}
          {hasMessages &&
            messages.map((message, index) => (
              <Box mb={1} key={index}>
                <Text size="small">{message}</Text>
              </Box>
            ))}
        </AccordionButton>
      </Stack>
    </Card>
  );
};

export default TelemetryTestCard;
